/**
 * /admin/permissions
 * Platform Admin only.
 *
 * Matrix editor: rows = invited emails, columns = pages (ordered by importance).
 * Each cell is a dropdown "— / View / Edit". Changes save on blur, no batch.
 *
 * Invite flow: "+ Invite email" opens a modal to whitelist a new email with an
 * initial permission set. An invitation email is sent automatically.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Mail, Plus, Trash2, RefreshCw, ShieldCheck, ShieldOff, Loader2, Send, Search, X, ChevronDown, ChevronRight, ChevronLeft, Zap, KeyRound,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_LABELS = { "": "—", view: "View", edit: "Edit" };
const LEVEL_COLOURS = {
  "": "text-slate-600",
  view: "text-cyan-300",
  edit: "text-emerald-300",
};

export default function AdminPermissions() {
  const [pages, setPages] = useState([]);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [filter, setFilter] = useState("");
  const [pendingCell, setPendingCell] = useState(null); // {email, page_key}
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pagesRes, invitesRes, reqRes] = await Promise.all([
        axios.get(`${API}/permissions/pages`),
        axios.get(`${API}/admin/invites`),
        axios.get(`${API}/admin/access-requests`).catch(() => ({ data: { requests: [] } })),
      ]);
      // Doserne Academy focused UI — hide non-Academy groups (Office
      // etc.) since this screen manages the Academy team's access.
      setPages((pagesRes.data.pages || []).filter((p) => p.group === "Academy"));
      setInvites(invitesRes.data.invites || []);
      setRequests(reqRes.data.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load permissions");
    }
    setLoading(false);
  }, []);

  const approveRequest = async (id) => {
    try {
      await axios.post(`${API}/admin/access-requests/${id}/approve`);
      toast.success("Godkjent — invitasjon sendt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };

  const rejectRequest = async (id) => {
    if (!window.confirm("Avvise denne forespørselen?")) return;
    try {
      await axios.post(`${API}/admin/access-requests/${id}/reject`);
      toast.success("Avvist");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const byGroup = {};
    for (const p of pages) {
      const g = p.group || "Other";
      if (!byGroup[g]) byGroup[g] = [];
      byGroup[g].push(p);
    }
    return byGroup;
  }, [pages]);

  const filteredInvites = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return invites;
    return invites.filter((i) => i.email.toLowerCase().includes(q) || (i.name || "").toLowerCase().includes(q));
  }, [invites, filter]);

  const setCell = async (email, pageKey, level) => {
    setPendingCell({ email, page_key: pageKey });
    // Optimistic
    setInvites((cur) => cur.map((i) => {
      if (i.email !== email) return i;
      const perms = { ...(i.permissions || {}) };
      if (!level) delete perms[pageKey]; else perms[pageKey] = level;
      return { ...i, permissions: perms };
    }));
    try {
      await axios.patch(`${API}/admin/invites/${encodeURIComponent(email)}/permission`,
        { page_key: pageKey, level: level || null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      load();
    }
    setPendingCell(null);
  };

  const deleteInvite = async (email) => {
    if (!window.confirm(`Remove ${email} completely?`)) return;
    try {
      await axios.delete(`${API}/admin/invites/${encodeURIComponent(email)}`);
      toast.success(`${email} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (email) => {
    try {
      const { data } = await axios.post(`${API}/admin/invites/${encodeURIComponent(email)}/toggle-active`);
      toast.success(data.is_active ? "Access enabled" : "Access blocked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const resendInvite = async (email) => {
    try {
      await axios.post(`${API}/admin/invites/${encodeURIComponent(email)}/resend`);
      toast.success(`Invitation resent to ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const sendPasswordReset = async (email) => {
    if (!window.confirm(`Send reset-lenke pentru parolă la ${email}?`)) return;
    try {
      const { data } = await axios.post(`${API}/doserne/admin/users/send-reset`, { email });
      toast.success(data?.message || `Reset link sent to ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende reset-lenke");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-slate-100 p-6">
      <Helmet><title>Permissions — AllStillas</title></Helmet>
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/doserne"
                className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 hover:scale-105 transition-transform flex-shrink-0"
                title="Back to Doserne Academy"
                data-testid="btn-back-to-doserne">
            <Zap className="w-5 h-5 text-slate-900" strokeWidth={3}/>
          </Link>
          <div>
            <Link to="/doserne"
                  className="text-[10px] uppercase tracking-widest text-cyan-400 font-black hover:text-cyan-300 inline-flex items-center gap-1"
                  data-testid="btn-back-to-doserne-label">
              <ChevronLeft className="w-3 h-3"/> Doserne · Admin
            </Link>
            <h1 className="text-3xl font-black">Access & Permissions</h1>
            <div className="text-sm text-slate-400 mt-1">
              Whitelist emails and set per-page access. Users self-serve via magic-link login at
              <code className="mx-1 text-cyan-300">/doserne/logg-inn</code>.
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500"/>
              <input value={filter} onChange={(e) => setFilter(e.target.value)}
                     placeholder="Search email or name…"
                     className="pl-8 pr-3 py-2 text-sm rounded-lg bg-white/5 border border-white/10 text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400 w-64"
                     data-testid="perm-search"/>
            </div>
            <button onClick={load}
                    className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"
                    title="Refresh"
                    data-testid="perm-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}/>
            </button>
            <button onClick={() => setShowInvite(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black text-sm uppercase tracking-widest"
                    data-testid="perm-new-invite">
              <Plus className="w-4 h-4"/> Invite email
            </button>
            <button onClick={() => setShowBulk(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-black text-sm uppercase tracking-widest"
                    data-testid="perm-bulk-invite">
              <Send className="w-4 h-4"/> Bulk invite
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 text-[10px] uppercase tracking-widest text-slate-500 font-black">
          <span>Legend:</span>
          <span className="text-slate-600">— No access</span>
          <span className="text-cyan-300">View · read-only</span>
          <span className="text-emerald-300">Edit · full access</span>
          <span className="ml-auto text-slate-500">Changes save automatically</span>
        </div>

        {/* Access requests — public "Be om tilgang" submissions */}
        {requests.filter((r) => r.status === "pending").length > 0 && (
          <div className="rounded-xl border-2 border-amber-500/40 bg-amber-500/[0.06] p-4" data-testid="access-requests-panel">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Mail className="w-4 h-4 text-amber-300"/>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Awaiting approval</div>
                <div className="text-base font-black text-slate-100">
                  {requests.filter((r) => r.status === "pending").length} tilgangsforespørsel(er)
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {requests.filter((r) => r.status === "pending").map((r) => (
                <div key={r.id}
                     className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-lg bg-black/40 border border-amber-500/20"
                     data-testid={`req-row-${r.id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-black text-slate-100">
                      {r.full_name || <span className="italic text-slate-500">(uten navn)</span>}
                    </div>
                    <div className="text-xs font-mono text-cyan-300">{r.email}</div>
                    {r.phone && <div className="text-[10px] text-slate-500">📞 {r.phone}</div>}
                    {r.message && (
                      <div className="text-xs text-slate-400 mt-1 italic border-l-2 border-amber-500/40 pl-2">
                        &ldquo;{r.message}&rdquo;
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-1">
                      {new Date(r.requested_at).toLocaleString("nb-NO")}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => approveRequest(r.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-[10px] uppercase tracking-widest font-black"
                            data-testid={`req-approve-${r.id}`}>
                      <ShieldCheck className="w-3 h-3"/> Godkjenn
                    </button>
                    <button onClick={() => rejectRequest(r.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-200 text-[10px] uppercase tracking-widest font-black"
                            data-testid={`req-reject-${r.id}`}>
                      <X className="w-3 h-3"/> Avvis
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Matrix — desktop */}
        {!isMobile ? (
        <div className="rounded-xl border border-white/10 bg-[#0f1a2e] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="perm-matrix">
              <thead>
                <tr className="bg-white/5 border-b border-white/10">
                  <th className="sticky left-0 z-10 bg-slate-800 px-4 py-3 text-left text-[10px] uppercase tracking-widest text-slate-300 font-black min-w-[280px] border-r border-slate-700">
                    Email · Name · Status
                  </th>
                  {Object.entries(groups).map(([groupName, groupPages]) => (
                    <React.Fragment key={groupName}>
                      <th colSpan={groupPages.length}
                          className="px-3 py-2 text-[9px] uppercase tracking-widest text-slate-500 font-black text-center border-b border-white/10 bg-slate-800/40">
                        {groupName}
                      </th>
                    </React.Fragment>
                  ))}
                  <th className="sticky right-0 z-10 bg-slate-800 px-4 py-3 text-right text-[10px] uppercase tracking-widest text-slate-300 font-black min-w-[140px] border-l border-slate-700">
                    Actions
                  </th>
                </tr>
                <tr className="border-b border-white/10 bg-slate-900/40">
                  <th className="sticky left-0 z-10 bg-slate-900 px-4 py-2 border-r border-slate-700"></th>
                  {pages.map((p) => (
                    <th key={p.key}
                        className="px-2 py-2 text-[10px] uppercase tracking-widest text-slate-400 font-black text-center whitespace-nowrap"
                        title={p.description}>
                      {p.label}
                    </th>
                  ))}
                  <th className="sticky right-0 z-10 bg-slate-900 px-4 py-2 border-l border-slate-700"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={pages.length + 2} className="px-4 py-16 text-center text-slate-500">
                      <Loader2 className="w-6 h-6 animate-spin inline-block mr-2"/> Loading…
                    </td>
                  </tr>
                )}
                {!loading && filteredInvites.length === 0 && (
                  <tr>
                    <td colSpan={pages.length + 2} className="px-4 py-16 text-center text-slate-500 italic">
                      No invites yet. Click <strong className="text-emerald-300">Invite email</strong> to add someone.
                    </td>
                  </tr>
                )}
                {filteredInvites.map((inv) => (
                  <tr key={inv.email} className={`border-b border-white/5 hover:bg-white/[.02] ${!inv.is_active ? "opacity-40" : ""}`}>
                    <td className="sticky left-0 bg-[#0f1a2e] px-4 py-2 border-r border-slate-700/40">
                      <div className="font-black text-slate-100 text-sm">{inv.email}</div>
                      <div className="text-[11px] text-slate-400">{inv.name || "—"}</div>
                      <div className="text-[10px] mt-0.5">
                        {inv.activated_at ? (
                          <span className="text-emerald-400">● Active · joined {(inv.activated_at || "").slice(0, 10)}</span>
                        ) : (
                          <span className="text-amber-400">● Pending invite</span>
                        )}
                        {!inv.is_active && <span className="text-red-400 ml-2">· Blocked</span>}
                      </div>
                    </td>
                    {pages.map((p) => {
                      const lvl = (inv.permissions || {})[p.key] || "";
                      const isPending = pendingCell?.email === inv.email && pendingCell?.page_key === p.key;
                      return (
                        <td key={p.key} className="px-2 py-2 text-center">
                          <select value={lvl}
                                  disabled={isPending}
                                  onChange={(e) => setCell(inv.email, p.key, e.target.value)}
                                  className={`text-[10px] uppercase tracking-widest font-black bg-transparent border border-white/10 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-cyan-400 ${LEVEL_COLOURS[lvl]}`}
                                  data-testid={`cell-${inv.email}-${p.key}`}>
                            <option value="" className="bg-slate-900 text-slate-600">— none</option>
                            <option value="view" className="bg-slate-900 text-cyan-300">View</option>
                            <option value="edit" className="bg-slate-900 text-emerald-300">Edit</option>
                          </select>
                        </td>
                      );
                    })}
                    <td className="sticky right-0 bg-[#0f1a2e] px-4 py-2 border-l border-slate-700/40">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => resendInvite(inv.email)}
                                className="p-1.5 rounded hover:bg-cyan-500/10 text-cyan-300"
                                title="Resend invitation email"
                                data-testid={`resend-${inv.email}`}>
                          <Send className="w-3.5 h-3.5"/>
                        </button>
                        <button onClick={() => sendPasswordReset(inv.email)}
                                className="p-1.5 rounded hover:bg-emerald-500/10 text-emerald-300"
                                title="Send password reset link"
                                data-testid={`reset-pw-${inv.email}`}>
                          <KeyRound className="w-3.5 h-3.5"/>
                        </button>
                        <button onClick={() => toggleActive(inv.email)}
                                className="p-1.5 rounded hover:bg-amber-500/10 text-amber-300"
                                title={inv.is_active ? "Block access" : "Restore access"}
                                data-testid={`toggle-${inv.email}`}>
                          {inv.is_active ? <ShieldOff className="w-3.5 h-3.5"/> : <ShieldCheck className="w-3.5 h-3.5"/>}
                        </button>
                        <button onClick={() => deleteInvite(inv.email)}
                                className="p-1.5 rounded hover:bg-red-500/10 text-red-300"
                                title="Delete completely"
                                data-testid={`delete-${inv.email}`}>
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        ) : (
          /* Matrix — mobile card view */
          <MobileInviteList pages={pages}
                            invites={filteredInvites}
                            groups={groups}
                            loading={loading}
                            pendingCell={pendingCell}
                            onSetCell={setCell}
                            onDelete={deleteInvite}
                            onToggle={toggleActive}
                            onResend={resendInvite}
                            onSendReset={sendPasswordReset}/>
        )}
      </div>

      {showInvite && (
        <InviteModal pages={pages} onClose={() => setShowInvite(false)} onCreated={load}/>
      )}
      {showBulk && (
        <BulkInviteModal pages={pages} onClose={() => setShowBulk(false)} onDone={load}/>
      )}
    </div>
  );
}

function InviteModal({ pages, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // Default preset "Muncitor tipic": every Doserne Academy page granted "edit"
  // so workers can use forms & lists normally. Office (incl. Capacity Planner)
  // stays empty — only office staff need that.
  const [permissions, setPermissions] = useState(() => {
    const preset = {};
    for (const p of pages) if (p.group === "Academy") preset[p.key] = "edit";
    return preset;
  });
  const [saving, setSaving] = useState(false);
  const [contract, setContract] = useState(null);    // {url, filename, size}
  const [uploading, setUploading] = useState(false);
  const [tempPassword, setTempPassword] = useState("");

  const setPage = (key, lvl) => {
    setPermissions((cur) => {
      const nx = { ...cur };
      if (!lvl) delete nx[key]; else nx[key] = lvl;
      return nx;
    });
  };

  const setPreset = (preset) => {
    if (preset === "clear") setPermissions({});
    else if (preset === "view-all") {
      const p = {};
      for (const pg of pages) p[pg.key] = "view";
      setPermissions(p);
    } else if (preset === "edit-office") {
      const p = {};
      for (const pg of pages) if (pg.group === "Office") p[pg.key] = "edit";
      setPermissions(p);
    } else if (preset === "muncitor") {
      // Muncitor tipic — full Academy access (edit), no Office/Capacity.
      const p = {};
      for (const pg of pages) if (pg.group === "Academy") p[pg.key] = "edit";
      setPermissions(p);
    }
  };

  const uploadContract = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Doar PDF acceptat"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Fișier prea mare (max 20MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/contracts/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setContract(data);
      toast.success(`Contract încărcat: ${data.filename}`);
    } catch { toast.error("Upload eșuat"); }
    setUploading(false);
  };

  const submit = async () => {
    if (!email.trim()) { toast.error("Email required"); return; }
    if (tempPassword && tempPassword.length < 6) { toast.error("Parola trebuie să aibă cel puțin 6 caractere"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/invites`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        permissions,
        contract_pdf_url: contract?.url || "",
        temp_password: tempPassword.trim() || "",
      });
      toast.success(`Invitation sent to ${email}` + (tempPassword ? " (cu parolă)" : "") + (contract ? " + contract" : ""));
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
    setSaving(false);
  };

  const suggestPassword = () => {
    // Friendly memorable password: word + 3 digits + symbol (workers will change later)
    const words = ["Stillas", "Klofta", "Oslo", "Norge", "Doserne", "Blues", "Green"];
    const w = words[Math.floor(Math.random() * words.length)];
    const n = 100 + Math.floor(Math.random() * 900);
    setTempPassword(`${w}${n}!`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-[#0f1a2e] border border-white/10 shadow-2xl">
        <div className="flex items-center gap-3 p-5 border-b border-white/10">
          <Mail className="w-5 h-5 text-cyan-300"/>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Invite by email</div>
            <div className="text-lg font-black text-slate-100">New whitelist entry</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5 text-slate-400 hover:text-slate-100">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="name@company.no"
                     className="mt-1 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                     data-testid="invite-email"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Display name (optional)</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Ion Popescu"
                     className="mt-1 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                     data-testid="invite-name"/>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
              Parolă inițială · muncitorul o poate schimba mai târziu
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="ex: Stillas123!"
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-100 font-mono focus:outline-none focus:ring-1 focus:ring-cyan-400"
                data-testid="invite-password"
              />
              <button
                type="button"
                onClick={suggestPassword}
                className="px-3 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-black uppercase tracking-widest"
                data-testid="invite-suggest-password"
              >
                🎲 Sugerează
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5">
              💡 Parola apare în emailul de invitație. Dacă e goală, muncitorul <strong>nu va putea intra</strong> fără o resetare.
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Employment contract (PDF, optional)</label>
            <div className="mt-1">
              {contract ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40" data-testid="invite-contract-attached">
                  <span className="text-lg">📎</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-emerald-200 truncate">{contract.filename}</div>
                    <div className="text-[10px] text-emerald-400/70">{Math.round(contract.size / 1024)} KB · atașat la invitație</div>
                  </div>
                  <button type="button" onClick={() => setContract(null)}
                          className="text-[10px] uppercase tracking-widest font-black text-red-300 hover:text-red-400"
                          data-testid="invite-contract-remove">Elimină</button>
                </div>
              ) : (
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-dashed border-white/20 hover:border-cyan-400 cursor-pointer text-sm text-slate-300">
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...</>
                  ) : (
                    <><span>📎</span> Atașează contractul PDF (opțional)</>
                  )}
                  <input type="file" accept="application/pdf" className="hidden"
                         onChange={(e) => uploadContract(e.target.files?.[0])}
                         data-testid="invite-contract-file"/>
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Quick presets</label>
            <div className="mt-1 flex flex-wrap gap-2">
              <button onClick={() => setPreset("muncitor")} className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 ring-1 ring-amber-500/40" data-testid="preset-muncitor">👷 Muncitor tipic</button>
              <button onClick={() => setPreset("clear")} className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-400">Clear</button>
              <button onClick={() => setPreset("view-all")} className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300">View all</button>
              <button onClick={() => setPreset("edit-office")} className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300">Edit office</button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5">
              💡 <strong className="text-amber-300">Muncitor tipic</strong> = acces complet la Academy (formulare, SJA, Kontroll, Underlag), fără Capacity Planner.
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Permissions</label>
            <div className="mt-2 max-h-72 overflow-y-auto pr-2 space-y-1">
              {pages.map((p) => {
                const lvl = permissions[p.key] || "";
                return (
                  <div key={p.key} className="flex items-center gap-3 p-2 rounded bg-white/[0.02] border border-white/5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-black text-slate-100">{p.label}</div>
                      <div className="text-[10px] text-slate-500">{p.group} · {p.description}</div>
                    </div>
                    <select value={lvl} onChange={(e) => setPage(p.key, e.target.value)}
                            className={`text-[10px] uppercase tracking-widest font-black bg-transparent border border-white/10 rounded px-2 py-1 ${LEVEL_COLOURS[lvl]}`}
                            data-testid={`invite-perm-${p.key}`}>
                      <option value="" className="bg-slate-900 text-slate-600">— none</option>
                      <option value="view" className="bg-slate-900 text-cyan-300">View</option>
                      <option value="edit" className="bg-slate-900 text-emerald-300">Edit</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 bg-slate-800/40 rounded-lg p-3">
            <strong className="text-slate-300">What happens next:</strong> An email will be sent to <code className="text-cyan-300">{email || "the address"}</code> in Romanian and English with a link to
            <code className="mx-1 text-cyan-300">/doserne/logg-inn</code>. On first login they will get a magic code and the account will be auto-activated with the permissions above.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-white/10 bg-slate-900/40">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black text-sm uppercase tracking-widest disabled:opacity-50"
                  data-testid="invite-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            Send invitation
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Mobile card view — one collapsible card per invitee
// ============================================================
function MobileInviteList({ pages, invites, groups, loading, pendingCell, onSetCell, onDelete, onToggle, onResend, onSendReset }) {
  const [open, setOpen] = React.useState(new Set());
  const toggleOpen = (email) => setOpen((cur) => {
    const nx = new Set(cur);
    if (nx.has(email)) nx.delete(email); else nx.add(email);
    return nx;
  });

  if (loading) return (
    <div className="rounded-xl border border-white/10 bg-[#0f1a2e] p-8 text-center text-slate-500">
      <Loader2 className="w-6 h-6 animate-spin inline-block mr-2"/> Loading…
    </div>
  );
  if (!invites.length) return (
    <div className="rounded-xl border border-white/10 bg-[#0f1a2e] p-8 text-center text-slate-500 italic">
      No invites yet. Tap <strong className="text-emerald-300">Invite email</strong> to add someone.
    </div>
  );

  return (
    <div className="space-y-2" data-testid="m-invite-list">
      {invites.map((inv) => {
        const isOpen = open.has(inv.email);
        const grantedCount = Object.keys(inv.permissions || {}).length;
        return (
          <div key={inv.email}
               className={`rounded-xl border ${!inv.is_active ? "opacity-50" : ""} bg-[#0f1a2e] overflow-hidden`}
               style={{ borderColor: "rgba(255,255,255,0.08)" }}>
            <button type="button"
                    onClick={() => toggleOpen(inv.email)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-left"
                    data-testid={`m-invite-header-${inv.email}`}>
              {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400"/> : <ChevronRight className="w-4 h-4 text-slate-400"/>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black text-slate-100 truncate">{inv.email}</div>
                <div className="text-[11px] text-slate-400">{inv.name || "—"}</div>
                <div className="text-[10px] mt-0.5">
                  {inv.activated_at
                    ? <span className="text-emerald-400">● Active</span>
                    : <span className="text-amber-400">● Pending</span>}
                  <span className="text-slate-500 ml-2">· {grantedCount} page{grantedCount !== 1 ? "s" : ""}</span>
                  {!inv.is_active && <span className="text-red-400 ml-2">· Blocked</span>}
                </div>
              </div>
            </button>

            {isOpen && (
              <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
                {/* Action bar */}
                <div className="flex items-center gap-2">
                  <button onClick={() => onResend(inv.email)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-300 text-[10px] uppercase tracking-widest font-black">
                    <Send className="w-3.5 h-3.5"/> Resend
                  </button>
                  <button onClick={() => onSendReset(inv.email)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-widest font-black"
                          data-testid={`m-reset-pw-${inv.email}`}>
                    <KeyRound className="w-3.5 h-3.5"/> Reset PW
                  </button>
                  <button onClick={() => onToggle(inv.email)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-300 text-[10px] uppercase tracking-widest font-black">
                    {inv.is_active ? <><ShieldOff className="w-3.5 h-3.5"/> Block</> : <><ShieldCheck className="w-3.5 h-3.5"/> Restore</>}
                  </button>
                  <button onClick={() => onDelete(inv.email)}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 text-red-300 text-[10px] uppercase tracking-widest font-black">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>

                {/* Permissions per group */}
                {Object.entries(groups).map(([groupName, groupPages]) => (
                  <div key={groupName}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">{groupName}</div>
                    <div className="space-y-1.5">
                      {groupPages.map((p) => {
                        const lvl = (inv.permissions || {})[p.key] || "";
                        const isPending = pendingCell?.email === inv.email && pendingCell?.page_key === p.key;
                        return (
                          <div key={p.key} className="flex items-center gap-2 py-1.5 border-b border-white/[0.03]">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-black text-slate-200">{p.label}</div>
                            </div>
                            <select value={lvl}
                                    disabled={isPending}
                                    onChange={(e) => onSetCell(inv.email, p.key, e.target.value)}
                                    className={`text-[10px] uppercase tracking-widest font-black bg-transparent border border-white/10 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-cyan-400 ${LEVEL_COLOURS[lvl]}`}
                                    data-testid={`m-cell-${inv.email}-${p.key}`}>
                              <option value="" className="bg-slate-900 text-slate-600">— none</option>
                              <option value="view" className="bg-slate-900 text-cyan-300">View</option>
                              <option value="edit" className="bg-slate-900 text-emerald-300">Edit</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}



// ============================================================
// Bulk-invite modal — table with one row per email, each row can
// have an OPTIONAL PDF employment contract attached. Shared perm
// matrix applies to everyone. Backend endpoint accepts the
// `rows: [{email, contract_pdf_url}]` shape natively.
// ============================================================
function BulkInviteModal({ pages, onClose, onDone }) {
  const [rows, setRows] = useState([{ id: 1, email: "", contract: null, uploading: false }]);
  // Default preset: "Muncitor tipic" — Academy pages edit, Office pages empty.
  const [perms, setPerms] = useState(() => {
    const preset = {};
    for (const p of pages) if (p.group === "Academy") preset[p.key] = "edit";
    return preset;
  });
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [sharedPassword, setSharedPassword] = useState("");

  const applyPreset = (preset) => {
    if (preset === "clear") setPerms({});
    else if (preset === "muncitor") {
      const p = {};
      for (const pg of pages) if (pg.group === "Academy") p[pg.key] = "edit";
      setPerms(p);
    } else if (preset === "view-all") {
      const p = {};
      for (const pg of pages) p[pg.key] = "view";
      setPerms(p);
    } else if (preset === "edit-office") {
      const p = {};
      for (const pg of pages) if (pg.group === "Office") p[pg.key] = "edit";
      setPerms(p);
    }
  };

  const validRowCount = useMemo(() => {
    const seen = new Set();
    let n = 0;
    for (const r of rows) {
      const e = (r.email || "").trim().toLowerCase();
      if (!e || seen.has(e)) continue;
      if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(e)) continue;
      seen.add(e);
      n++;
    }
    return n;
  }, [rows]);

  const setLevel = (page_key, level) => {
    setPerms((prev) => {
      const next = { ...prev };
      if (!level) delete next[page_key]; else next[page_key] = level;
      return next;
    });
  };

  const setEmail = (id, value) => setRows((cur) => cur.map((r) => r.id === id ? { ...r, email: value } : r));

  const addRow = () => setRows((cur) => [...cur, { id: (cur[cur.length - 1]?.id || 0) + 1, email: "", contract: null, uploading: false }]);

  const removeRow = (id) => setRows((cur) => cur.length === 1 ? cur : cur.filter((r) => r.id !== id));

  const uploadRowContract = async (id, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Doar PDF acceptat"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Fișier prea mare (max 20MB)"); return; }
    setRows((cur) => cur.map((r) => r.id === id ? { ...r, uploading: true } : r));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/contracts/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRows((cur) => cur.map((r) => r.id === id ? { ...r, contract: data, uploading: false } : r));
    } catch {
      toast.error("Upload eșuat");
      setRows((cur) => cur.map((r) => r.id === id ? { ...r, uploading: false } : r));
    }
  };

  const clearRowContract = (id) => setRows((cur) => cur.map((r) => r.id === id ? { ...r, contract: null } : r));

  // Paste handler: if user pastes into the first email input a big blob of
  // emails, auto-fan into individual rows (only when it looks like multiple).
  const handlePaste = (id, e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text");
    if (!text) return;
    const parts = text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length <= 1) return;
    e.preventDefault();
    setRows((cur) => {
      const nx = cur.filter((r) => r.id !== id && (r.email || r.contract));
      let maxId = nx.reduce((m, r) => Math.max(m, r.id), 0);
      const seen = new Set(nx.map((r) => (r.email || "").trim().toLowerCase()).filter(Boolean));
      for (const p of parts) {
        const em = p.toLowerCase();
        if (seen.has(em)) continue;
        seen.add(em);
        maxId += 1;
        nx.push({ id: maxId, email: em, contract: null, uploading: false });
      }
      if (nx.length === 0) nx.push({ id: maxId + 1, email: "", contract: null, uploading: false });
      return nx;
    });
  };

  const submit = async () => {
    if (validRowCount === 0) { toast.error("Adaugă cel puțin un email valid"); return; }
    if (sharedPassword && sharedPassword.length < 6) { toast.error("Parola trebuie să aibă cel puțin 6 caractere"); return; }
    setBusy(true);
    try {
      const payloadRows = rows
        .map((r) => ({ email: (r.email || "").trim().toLowerCase(), contract_pdf_url: r.contract?.url || "" }))
        .filter((r) => r.email);
      const { data } = await axios.post(`${API}/admin/invites/bulk`, {
        rows: payloadRows,
        permissions: perms,
        shared_password: sharedPassword.trim() || "",
      });
      setResults(data);
      if (data.sent > 0) toast.success(`${data.sent} invitații trimise`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    }
    setBusy(false);
  };

  const finish = () => {
    onDone();
    onClose();
  };

  // Results view
  if (results) {
    return (
      <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={finish}>
        <div className="bg-[#0a0a0a] w-full max-w-2xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bulk-results-modal">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Bulk invite</div>
              <h3 className="text-lg font-black text-white">Rezultate</h3>
            </div>
            <button onClick={finish} className="text-slate-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
          </div>

          <div className="px-5 py-3 border-b border-white/10 grid grid-cols-5 gap-3">
            <Stat label="Total"       value={results.total}      color="text-slate-200" />
            <Stat label="Trimise"     value={results.sent}       color="text-emerald-400" />
            <Stat label="+ contract"  value={results.with_contract || 0} color="text-cyan-400" />
            <Stat label="Duplicate"   value={results.duplicates} color="text-amber-400" />
            <Stat label="Invalide"    value={(results.invalid || 0) + (results.errors || 0)} color="text-red-400" />
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            <ul className="space-y-1.5">
              {(results.results || []).map((r, i) => (
                <li key={i}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
                      r.status === "sent"      ? "border-emerald-500/40 bg-emerald-500/5" :
                      r.status === "duplicate" ? "border-amber-500/40 bg-amber-500/5" :
                                                 "border-red-500/40 bg-red-500/5"
                    }`}
                    data-testid={`bulk-result-${i}`}>
                  <span className="text-lg">
                    {r.status === "sent" ? "✓" : r.status === "duplicate" ? "⚠" : "✗"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-white truncate">{r.email}</div>
                    <div className="text-[10px] uppercase tracking-widest font-black text-slate-400">
                      {r.status === "sent" ? "trimis" : r.status === "duplicate" ? "duplicat" : r.status === "invalid" ? "invalid" : "eroare"} · {r.message}
                    </div>
                  </div>
                  {r.with_contract && (
                    <span className="text-[10px] uppercase tracking-widest font-black text-cyan-300 border border-cyan-500/40 rounded px-1.5 py-0.5">
                      📎 contract
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10 bg-white/[0.02]">
            <button onClick={() => { setResults(null); setRows([{ id: 1, email: "", contract: null, uploading: false }]); }}
                    className="px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-lg"
                    data-testid="bulk-invite-again">
              Trimite altele
            </button>
            <button onClick={finish}
                    className="px-4 py-2 text-sm font-black uppercase tracking-widest bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-lg"
                    data-testid="bulk-close">
              Închide
            </button>
          </div>
        </div>
      </div>
    );
  }

  const attachedCount = rows.filter((r) => r.contract).length;

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0a0a] w-full max-w-4xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bulk-invite-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Platform admin</div>
            <h3 className="text-lg font-black text-white">Bulk invite</h3>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Câte un rând per invitat · atașează contract PDF opțional pe fiecare rând
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="bulk-close-x"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Rows table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-400 font-black">
                Invitați ({validRowCount} valid{validRowCount === 1 ? "" : "e"}{attachedCount > 0 ? ` · ${attachedCount} cu contract` : ""})
              </label>
              <button onClick={addRow}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-[10px] uppercase tracking-widest font-black text-slate-300"
                      data-testid="bulk-add-row">
                <Plus className="w-3 h-3" /> Adaugă rând
              </button>
            </div>

            <div className="rounded-lg border border-white/10 overflow-hidden">
              <div className="hidden md:grid grid-cols-[1fr_1.4fr_auto] gap-2 items-center px-3 py-2 bg-white/5 text-[10px] uppercase tracking-widest text-slate-500 font-black">
                <div>Email</div>
                <div>Contract PDF (opțional)</div>
                <div className="w-8"></div>
              </div>
              <div className="divide-y divide-white/5 max-h-[280px] overflow-y-auto">
                {rows.map((r, idx) => (
                  <div key={r.id} className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr_auto] gap-2 items-center px-3 py-2" data-testid={`bulk-row-${idx}`}>
                    <input
                      type="email"
                      value={r.email}
                      onChange={(e) => setEmail(r.id, e.target.value)}
                      onPaste={(e) => handlePaste(r.id, e)}
                      placeholder="nume@doserne.com"
                      className="px-2.5 py-1.5 rounded-lg bg-black/60 border border-white/10 text-white text-sm font-mono focus:outline-none focus:ring-1 focus:ring-emerald-400 placeholder:text-slate-600"
                      data-testid={`bulk-row-email-${idx}`}
                    />
                    <div>
                      {r.contract ? (
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/40" data-testid={`bulk-row-contract-${idx}`}>
                          <span>📎</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-mono text-emerald-200 truncate">{r.contract.filename}</div>
                            <div className="text-[9px] text-emerald-400/70">{Math.round(r.contract.size / 1024)} KB</div>
                          </div>
                          <button type="button" onClick={() => clearRowContract(r.id)}
                                  className="text-[9px] uppercase tracking-widest font-black text-red-300 hover:text-red-400"
                                  data-testid={`bulk-row-contract-remove-${idx}`}>Elimină</button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/5 border border-dashed border-white/20 hover:border-cyan-400 cursor-pointer text-[11px] text-slate-400">
                          {r.uploading ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> Se încarcă...</>
                          ) : (
                            <><span>📎</span> Atașează PDF</>
                          )}
                          <input type="file" accept="application/pdf" className="hidden"
                                 onChange={(e) => uploadRowContract(r.id, e.target.files?.[0])}
                                 data-testid={`bulk-row-contract-file-${idx}`}/>
                        </label>
                      )}
                    </div>
                    <button onClick={() => removeRow(r.id)}
                            disabled={rows.length === 1}
                            className="p-1.5 rounded hover:bg-red-500/10 text-red-300 disabled:opacity-30 disabled:cursor-not-allowed justify-self-end"
                            data-testid={`bulk-row-remove-${idx}`}
                            title="Șterge rând">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              💡 Poți lipi mai multe emailuri deodată în orice rând — vor fi fanout automat pe rânduri separate.
            </div>
          </div>

          {/* Shared permissions */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-2">
              Parolă inițială partajată · toți primesc aceeași parolă (opțional)
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={sharedPassword}
                onChange={(e) => setSharedPassword(e.target.value)}
                placeholder="ex: Doserne2026!"
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-100 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                data-testid="bulk-shared-password"
              />
              <button
                type="button"
                onClick={() => {
                  const words = ["Stillas", "Doserne", "Klofta", "Oslo"];
                  const w = words[Math.floor(Math.random() * words.length)];
                  const n = 100 + Math.floor(Math.random() * 900);
                  setSharedPassword(`${w}${n}!`);
                }}
                className="px-3 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-black uppercase tracking-widest"
                data-testid="bulk-suggest-password"
              >
                🎲 Sugerează
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mt-1.5">
              💡 Fiecare muncitor primește parola în emailul lui + o poate schimba din Profil.
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">
                Permisiuni aplicate tuturor
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => applyPreset("muncitor")}
                        className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 ring-1 ring-amber-500/40"
                        data-testid="bulk-preset-muncitor">
                  👷 Muncitor tipic
                </button>
                <button type="button" onClick={() => applyPreset("clear")}
                        className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 text-slate-400">
                  Clear
                </button>
                <button type="button" onClick={() => applyPreset("view-all")}
                        className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300">
                  View all
                </button>
                <button type="button" onClick={() => applyPreset("edit-office")}
                        className="text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300">
                  Edit office
                </button>
              </div>
            </div>
            <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
              {pages.map((p) => (
                <div key={p.key} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-white/10 bg-black/40">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-white truncate">{p.label}</div>
                    <div className="text-[10px] text-slate-500 truncate">{p.group}</div>
                  </div>
                  <select
                    value={perms[p.key] || ""}
                    onChange={(e) => setLevel(p.key, e.target.value)}
                    className="bg-black/60 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none"
                    data-testid={`bulk-perm-${p.key}`}
                  >
                    <option value="">—</option>
                    <option value="view">View</option>
                    <option value="edit">Edit</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10 bg-white/[0.02]">
          <button onClick={onClose}
                  className="px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-lg">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || validRowCount === 0 || rows.some((r) => r.uploading)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-black uppercase tracking-widest bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 rounded-lg"
            data-testid="bulk-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Trimite {validRowCount > 0 ? `(${validRowCount}${attachedCount > 0 ? ` · ${attachedCount} 📎` : ""})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <div className={`text-2xl font-black leading-none ${color}`}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest font-black text-slate-500 mt-1">{label}</div>
    </div>
  );
}
